import React, { useState } from 'react'
import { Breadcrumb, DatePicker, Drawer, Layout, PageHeader, Select, Tabs, Tag } from 'antd'
import { useQuery } from '@tanstack/react-query'

import MainLayout from '~/components/mainLayout'
import DataTable from '~/components/dataTable'
import {
	exportAccessLogs,
	exportDocumentLogs,
	getAccessLogs,
	getDocumentLogs,
	getUsersOptions,
} from 'services/accessLogs/accessLogsService'
import { errorMessage } from 'services/messager'

const { RangePicker } = DatePicker
const { TabPane } = Tabs

const accessColumns = [
	{ title: 'Nome', dataIndex: 'user_name', key: 'user_name' },
	{ title: 'E-mail', dataIndex: 'user_email', key: 'user_email' },
	{
		title: 'Data/Hora do Acesso',
		dataIndex: 'created_at',
		key: 'created_at',
		render: (value) => (value ? new Date(value).toLocaleString('pt-BR') : '-'),
	},
	{
		title: 'SSO',
		dataIndex: 'sso',
		key: 'sso',
		render: (value) =>
			value ? <Tag color="blue">Sim</Tag> : <Tag color="default">Não</Tag>,
	},
	{ title: 'IP', dataIndex: 'ip_address', key: 'ip_address' },
]

const actionColumns = [
	{ title: 'Autor', dataIndex: 'user_name', key: 'user_name' },
	{ title: 'Ação', dataIndex: 'title', key: 'title' },
	{ title: 'Fluxo', dataIndex: 'flow', key: 'flow' },
	{
		title: 'Data de Alteração',
		dataIndex: 'created_at',
		key: 'created_at',
		render: (value) => (value ? new Date(value).toLocaleString('pt-BR') : '-'),
	},
]

const fetchAccessLogs = async ({ page, perPage, userId, fromDate, toDate }) => {
	try {
		return await getAccessLogs({ page, perPage, userId, fromDate, toDate })
	} catch (error) {
		errorMessage({ content: 'Erro ao obter logs de acesso' })
		return { items: [], page: 1, per_page: perPage, total: 0 }
	}
}

const fetchDocumentLogs = async ({ page, perPage, userId, fromDate, toDate }) => {
	try {
		return await getDocumentLogs({ page, perPage, userId, fromDate, toDate })
	} catch (error) {
		errorMessage({ content: 'Erro ao obter logs de ações' })
		return { items: [], page: 1, per_page: perPage, total: 0 }
	}
}

const fetchUsersOptions = async () => {
	try {
		return await getUsersOptions()
	} catch (error) {
		errorMessage({ content: 'Erro ao obter usuários' })
		return []
	}
}

const AccessLogs = () => {
	const [page, setPage] = useState(1)
	const [perPage, setPerPage] = useState(20)
	const [userId, setUserId] = useState(null)
	const [dateRange, setDateRange] = useState(null)
	const [exporting, setExporting] = useState(false)

	const [actionPage, setActionPage] = useState(1)
	const [actionPerPage, setActionPerPage] = useState(20)
	const [actionUserId, setActionUserId] = useState(null)
	const [actionDateRange, setActionDateRange] = useState(null)
	const [actionExporting, setActionExporting] = useState(false)
	const [selectedAction, setSelectedAction] = useState(null)

	const [fromDate, toDate] = dateRange || []
	const [actionFromDate, actionToDate] = actionDateRange || []

	const { data, isLoading } = useQuery({
		queryKey: ['access-logs', page, perPage, userId, fromDate, toDate],
		queryFn: () =>
			fetchAccessLogs({
				page,
				perPage,
				userId,
				fromDate: fromDate ? fromDate.format('YYYY-MM-DD') : null,
				toDate: toDate ? toDate.format('YYYY-MM-DD') : null,
			}),
	})

	const { data: actionData, isLoading: isActionLoading } = useQuery({
		queryKey: [
			'document-logs',
			actionPage,
			actionPerPage,
			actionUserId,
			actionFromDate,
			actionToDate,
		],
		queryFn: () =>
			fetchDocumentLogs({
				page: actionPage,
				perPage: actionPerPage,
				userId: actionUserId,
				fromDate: actionFromDate ? actionFromDate.format('YYYY-MM-DD') : null,
				toDate: actionToDate ? actionToDate.format('YYYY-MM-DD') : null,
			}),
	})

	const { data: usersOptions } = useQuery({
		queryKey: ['access-logs-users-options'],
		queryFn: fetchUsersOptions,
	})

	const handleExport = async () => {
		try {
			setExporting(true)
			await exportAccessLogs({
				userId,
				fromDate: fromDate ? fromDate.format('YYYY-MM-DD') : null,
				toDate: toDate ? toDate.format('YYYY-MM-DD') : null,
			})
		} catch (error) {
			errorMessage({ content: 'Erro ao exportar logs de acesso' })
		} finally {
			setExporting(false)
		}
	}

	const handleActionExport = async () => {
		try {
			setActionExporting(true)
			await exportDocumentLogs({
				userId: actionUserId,
				fromDate: actionFromDate ? actionFromDate.format('YYYY-MM-DD') : null,
				toDate: actionToDate ? actionToDate.format('YYYY-MM-DD') : null,
			})
		} catch (error) {
			errorMessage({ content: 'Erro ao exportar logs de ações' })
		} finally {
			setActionExporting(false)
		}
	}

	return (
		<MainLayout selectedKey="accessLogs">
			<Layout style={{ backgroundColor: '#fff' }}>
				<PageHeader>
					<Breadcrumb>
						<Breadcrumb.Item>Logs de Auditoria</Breadcrumb.Item>
					</Breadcrumb>
				</PageHeader>
				<Layout style={{ backgroundColor: '#fff', padding: '0 24px 24px' }}>
					<Tabs defaultActiveKey="access">
						<TabPane tab="Acessos" key="access">
							<DataTable
								columns={accessColumns}
								dataSource={data?.items || []}
								pages={{
									page: data?.page || page,
									per_page: data?.per_page || perPage,
									total: data?.total || 0,
								}}
								onChangePageNumber={({ page: nextPage, perPage: nextPerPage }) => {
									setPage(nextPage)
									setPerPage(nextPerPage)
								}}
								onSearch={() => {}}
								onClickButton={() => {}}
								textButton={false}
								placeholderNoData={!isLoading ? 'Nenhum acesso registrado' : ''}
								loading={isLoading}
								buttons={[
									{
										title: exporting ? 'Exportando...' : 'Exportar Excel',
										onClick: handleExport,
									},
								]}
								extraComponent={
									<div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
										<Select
											allowClear
											showSearch
											placeholder="Filtrar por usuário"
											optionFilterProp="label"
											style={{ minWidth: '220px' }}
											options={usersOptions || []}
											value={userId}
											onChange={(value) => {
												setPage(1)
												setUserId(value || null)
											}}
										/>
										<RangePicker
											format="DD/MM/YYYY"
											placeholder={['Data inicial', 'Data final']}
											value={dateRange}
											onChange={(value) => {
												setPage(1)
												setDateRange(value)
											}}
										/>
									</div>
								}
							/>
						</TabPane>
						<TabPane tab="Ações" key="actions">
							<DataTable
								columns={actionColumns}
								dataSource={actionData?.items || []}
								pages={{
									page: actionData?.page || actionPage,
									per_page: actionData?.per_page || actionPerPage,
									total: actionData?.total || 0,
								}}
								onChangePageNumber={({ page: nextPage, perPage: nextPerPage }) => {
									setActionPage(nextPage)
									setActionPerPage(nextPerPage)
								}}
								onSearch={() => {}}
								onClickButton={() => {}}
								textButton={false}
								placeholderNoData={!isActionLoading ? 'Nenhuma ação registrada' : ''}
								loading={isActionLoading}
								buttons={[
									{
										title: actionExporting ? 'Exportando...' : 'Exportar Excel',
										onClick: handleActionExport,
									},
								]}
								onRow={(record) => ({
									onClick: () => setSelectedAction(record),
									style: { cursor: 'pointer' },
								})}
								extraComponent={
									<div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
										<Select
											allowClear
											showSearch
											placeholder="Filtrar por usuário"
											optionFilterProp="label"
											style={{ minWidth: '220px' }}
											options={usersOptions || []}
											value={actionUserId}
											onChange={(value) => {
												setActionPage(1)
												setActionUserId(value || null)
											}}
										/>
										<RangePicker
											format="DD/MM/YYYY"
											placeholder={['Data inicial', 'Data final']}
											value={actionDateRange}
											onChange={(value) => {
												setActionPage(1)
												setActionDateRange(value)
											}}
										/>
									</div>
								}
							/>
						</TabPane>
					</Tabs>
				</Layout>
			</Layout>
			<Drawer
				title={selectedAction?.title}
				open={!!selectedAction}
				onClose={() => setSelectedAction(null)}
				width={420}>
				{selectedAction && (
					<>
						<p>
							<b>Autor:</b> {selectedAction.user_name}
							{selectedAction.user_email ? ` (${selectedAction.user_email})` : ''}
						</p>
						<p>
							<b>Fluxo:</b> {selectedAction.flow}
						</p>
						<p>
							<b>Data:</b>{' '}
							{selectedAction.created_at
								? new Date(selectedAction.created_at).toLocaleString('pt-BR')
								: '-'}
						</p>
						<p>
							<b>Detalhes:</b>
						</p>
						<p style={{ whiteSpace: 'pre-wrap' }}>{selectedAction.description}</p>
					</>
				)}
			</Drawer>
		</MainLayout>
	)
}

export default AccessLogs
